import { ALL_ASSET_CLASSES } from '@vizit/api';
import type { ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields } from '@vizit/api';
import type { AssetClass, SortDirection } from '@vizit/api';
import type { CollectionImageView, CollectionImagesPairParams } from '@vizit/api';
import { readPairScore } from '@vizit/api';
import { isHeroUnscored } from '@vizit/utils';

type CollectionImageSortField = ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields;

/**
 * The score field a collection's images are sorted by. Resolves to `'vizit_score'` today;
 * widens to include `'hero_score'` automatically once the API exposes it as a sortable field
 * (after `pnpm generate`), so hero collections can sort by the hero score shown on their cards.
 */
type CollectionImageScoreField = Extract<CollectionImageSortField, 'vizit_score' | 'hero_score'>;

/**
 * The scores the grid shows when the page picks them (custom benchmarks): the (benchmark, product category) pair
 * the tiles are scored against, `null` while the benchmark has no category, and whether that is known yet. While it
 * is not, the grid waits with skeletons instead of reading a list it would throw away.
 */
interface CollectionGridScoring {
  pair: CollectionImagesPairParams | null;
  isResolved: boolean;
  /** Images just uploaded that have no score under the pair yet; their tiles stay dimmed */
  waitingImageIds?: string[];
  /**
   * When the pair's scoring was last requested, by the API's clock. An image uploaded since waits for its score though
   * the page did not see the upload, e.g. after leaving the collection and coming back
   */
  requestedAt?: string | null;
  /**
   * A custom benchmark is picked, so without a pair (no category under it, or its categories failed to load) the tiles
   * show no score rather than each image's own, ranked against a Vizit benchmark
   */
  isCustomBenchmark?: boolean;
  /**
   * No scoring can run: the benchmark has no category to score against and, under a Vizit one, neither has the
   * collection. The tiles say so instead of waiting for a score, and the images are not re-read for one
   */
  needsCategory?: boolean;
}

/** Whether a pair score was read for the pair on screen: same category, same benchmark (Vizit by retailer). */
function isScoreForPair(pairScore: CollectionImageView['pair_score'], pair: CollectionImagesPairParams): boolean {
  if (!pairScore || pairScore.product_category_id !== pair.product_category_id) {
    return false;
  }
  return pair.retailer ? pairScore.retailer === pair.retailer : pairScore.benchmark_id === pair.benchmark_id;
}

/** Whether two pair reads score against the same benchmark; switching benchmark reloads the grid, a category not. */
function isSameBenchmark(a?: CollectionImagesPairParams | null, b?: CollectionImagesPairParams | null) {
  return (a?.benchmark_id ?? null) === (b?.benchmark_id ?? null) && (a?.retailer ?? null) === (b?.retailer ?? null);
}

enum CollectionAssetSortOption {
  ScoreDesc = 'score_desc',
  ScoreAsc = 'score_asc',
  DateNewest = 'date_newest',
}

const DEFAULT_ASSET_SORT = CollectionAssetSortOption.DateNewest;

const DEFAULT_SCORE_FIELD: CollectionImageScoreField = 'vizit_score';

function mapSortOptionToQueryParams(
  option: CollectionAssetSortOption,
  scoreField: CollectionImageScoreField,
): { sort_by: CollectionImageSortField; sort_direction: SortDirection } {
  switch (option) {
    case CollectionAssetSortOption.ScoreDesc:
      return { sort_by: scoreField, sort_direction: 'desc' };
    case CollectionAssetSortOption.ScoreAsc:
      return { sort_by: scoreField, sort_direction: 'asc' };
    default:
      return { sort_by: 'created_at', sort_direction: 'desc' };
  }
}

function parseCollectionAssetSort(value: unknown): CollectionAssetSortOption | undefined {
  const option = value as CollectionAssetSortOption;
  // The default sort is represented by an absent param, so it never persists in the URL.
  return option !== DEFAULT_ASSET_SORT && (Object.values(CollectionAssetSortOption) as string[]).includes(option)
    ? option
    : undefined;
}

function parseSparkHidden(value: unknown): boolean {
  return value === 'hidden';
}

function parseFavoritesOnly(value: unknown): boolean {
  return value === 'only';
}

/**
 * Parses the asset-class filter from the URL. Returns the class when it is a recognized
 * `AssetClass`, otherwise `undefined` (no filter). Absent/invalid values never persist.
 *
 * Validates against `ALL_ASSET_CLASSES` — the full union, including `Hero Image` — rather
 * than the selectable subset, since a hero collection can legitimately be filtered by it.
 * The picker's options come from the API (the classes actually present in the collection),
 * never from this list.
 */
function parseCollectionClassification(value: unknown): AssetClass | undefined {
  return typeof value === 'string' && (ALL_ASSET_CLASSES as readonly string[]).includes(value)
    ? (value as AssetClass)
    : undefined;
}

/**
 * Maps the selected asset class to the API's repeatable `classification` filter param.
 * `null`/`undefined` (i.e. "All asset classes") clears the filter.
 */
function mapClassificationToQueryParam(classification?: AssetClass | null): AssetClass[] | undefined {
  return classification ? [classification] : undefined;
}

/**
 * Reconciles a URL-supplied asset class against the classes actually present in the collection.
 *
 * `parseCollectionClassification` only proves a URL value is a real `AssetClass`, not that this
 * collection contains it — so a stale or shared link (e.g. `?assetClass=Lifestyle` on a collection
 * with no Lifestyle images) would filter the grid to empty while the picker is hidden, leaving no
 * way to clear it. This collapses such a value to "All" (`undefined`).
 *
 * While `availableClasses` is still loading (`isResolved` false) the URL value is trusted, so a
 * valid filter isn't dropped — and the grid isn't briefly refetched unfiltered — mid-load.
 */
function resolveActiveClassification(
  classification: AssetClass | undefined,
  availableClasses: AssetClass[],
  isResolved: boolean,
): AssetClass | undefined {
  if (!isResolved) {
    return classification;
  }
  return classification && availableClasses.includes(classification) ? classification : undefined;
}

interface TileScore {
  score: number | null;
  isPending: boolean;
  isDimmed: boolean;
  isUnscored: boolean;
  /** No scoring can run without a category, so the tile says so rather than waiting for a score */
  needsCategory: boolean;
}

function readTileScore(
  image: CollectionImageView,
  pair?: CollectionImagesPairParams | null,
  {
    isAwaitingUpload = false,
    pairRequestedAt,
    isCustomBenchmark = false,
    needsCategory = false,
  }: {
    isAwaitingUpload?: boolean;
    pairRequestedAt?: string | null;
    isCustomBenchmark?: boolean;
    needsCategory?: boolean;
  } = {},
): TileScore {
  // Under a Vizit benchmark an image's own score still shows. An image whose file has not landed waits like any other,
  // and one left out of scoring reads Unscored, as a category would not score it either.
  if (!pair && needsCategory && (isCustomBenchmark || image.display_score == null)) {
    const hasFile = !!image.url;
    return {
      score: null,
      isPending: !hasFile,
      isDimmed: false,
      isUnscored: hasFile,
      needsCategory: hasFile && !image.omit_score,
    };
  }
  // The display score is ranked against another benchmark, so it would read as the picked one's.
  if (!pair && isCustomBenchmark) {
    return { score: null, isPending: false, isDimmed: false, isUnscored: false, needsCategory: false };
  }
  if (!pair) {
    const score = image.display_score ?? null;
    const isPending = !image.url || image.score_status !== 'SCORED';
    return {
      score,
      isPending,
      isDimmed: false,
      isUnscored: isHeroUnscored({ omitScore: image.omit_score, score, isPending }),
      needsCategory: false,
    };
  }
  const pairScore = isScoreForPair(image.pair_score, pair) ? image.pair_score : null;
  const reading = readPairScore(pairScore, { isAwaitingUpload, imageCreatedAt: image.created_at, pairRequestedAt });
  const isPending = !image.url || reading.isScoring;
  return {
    score: reading.score,
    isPending,
    isDimmed: !image.url || (!!pairScore && reading.isScoring) || reading.isAwaitingUpload,
    isUnscored: isHeroUnscored({ omitScore: image.omit_score, score: reading.score, isPending }),
    needsCategory: false,
  };
}

/**
 * Whether an image without a score can still get one, so its list keeps re-reading it: under a pair, or under a Vizit
 * benchmark that falls back on the images' own scores. Without page-picked scoring the images' own scores always can.
 */
function awaitsTileScores(scoring?: CollectionGridScoring | null): boolean {
  return !scoring || !!scoring.pair || (!scoring.isCustomBenchmark && !scoring.needsCategory);
}

/** An image whose tile reads "Unscored" under the pair on screen. */
interface UnscoredImage {
  id: string;
  name: string;
}

/** The images whose tiles read "Unscored", with the pair they were read under, so a stale report can be told apart. */
interface UnscoredImagesReport {
  pair: CollectionImagesPairParams;
  images: UnscoredImage[];
}

/** Whether two pair reads name the same pair: same category, same benchmark. */
function isSamePair(a?: CollectionImagesPairParams | null, b?: CollectionImagesPairParams | null) {
  return !!a && !!b && a.product_category_id === b.product_category_id && isSameBenchmark(a, b);
}

/**
 * The images whose tiles read "Unscored" under the pair on screen: the pair settled without scoring them. A hero whose
 * score is hidden on purpose is left out. `null` without a pair, as the tiles then show no pair scores.
 */
function findUnscoredImages(
  images: CollectionImageView[],
  scoring?: CollectionGridScoring,
): UnscoredImagesReport | null {
  const pair = scoring?.pair;
  if (!pair) {
    return null;
  }
  const unscored = images.filter(
    (image) =>
      !image.omit_score &&
      readTileScore(image, pair, {
        isAwaitingUpload: !!scoring.waitingImageIds?.includes(image.id),
        pairRequestedAt: scoring.requestedAt,
      }).isUnscored,
  );
  return { pair, images: unscored.map(({ id, name }) => ({ id, name })) };
}

export {
  CollectionAssetSortOption,
  awaitsTileScores,
  isSameBenchmark,
  isScoreForPair,
  DEFAULT_ASSET_SORT,
  DEFAULT_SCORE_FIELD,
  findUnscoredImages,
  isSamePair,
  mapClassificationToQueryParam,
  mapSortOptionToQueryParams,
  parseCollectionAssetSort,
  parseCollectionClassification,
  parseFavoritesOnly,
  parseSparkHidden,
  readTileScore,
  resolveActiveClassification,
};
export type { CollectionGridScoring, CollectionImageScoreField, UnscoredImage, UnscoredImagesReport };
